import { RoutineWorkflow, useRoutineAlerts } from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import { Button } from '../../crop-circle/Button/Button';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import LoadingIndicator from '../../loadingIndicator';
import { BottomComponentBar } from '../../navigation/bottomComponentBar';
import { AlertSummary } from './alertSummary';
import { EditAlertModal } from './editAlertModal';
import { NewAlertModal } from './newAlertModal';

type AlertListProps = {
  workspaceAssetId: string;
  routineId: string;
  newAlertModalVisible: boolean;
  editAlertModalVisible: boolean;
  editAlertModalStyle: StyleProp<ViewStyle>;
  onClose: () => void;
  handleShowNewAlertModal: () => void;
  handleCloseNewAlertModal: () => void;
  handleShowEditAlertModal: () => void;
  handleCloseEditAlertModal: () => void;
  productId?: string;
};

export const AlertList = ({
  workspaceAssetId,
  routineId,
  newAlertModalVisible,
  editAlertModalVisible,
  editAlertModalStyle,
  onClose,
  handleShowNewAlertModal,
  handleCloseNewAlertModal,
  handleShowEditAlertModal,
  handleCloseEditAlertModal,
  productId,
}: AlertListProps) => {
  const { t } = useTranslation();
  const [alertSummaryId, setAlertSummaryId] = useState<string>('');

  const {
    routineWorkflows,
    workspaceAsset,
    isLoading: isRoutineAlertsLoading,
  } = useRoutineAlerts(workspaceAssetId, routineId, productId);

  const onAlertSelected = (alert: RoutineWorkflow) => {
    const alertId = alert?.id;

    if (!alertId) return;

    setAlertSummaryId(alertId);
    handleShowEditAlertModal();
  };

  const RenderAlerts = () => {
    return routineWorkflows?.map((item: any, index: number) => {
      return (
        <AlertSummary
          onAlertSelected={onAlertSelected}
          key={`${index}-${item.id}`}
          workflow={item}
          workspaceAsset={workspaceAsset}
          routineId={routineId}
        />
      );
    });
  };

  const isLoading = isRoutineAlertsLoading;

  return (
    <>
      <View style={styles.container}>
        {isLoading ? (
          <View
            style={{
              flex: 1,
            }}
          >
            <LoadingIndicator
              backgroundColor={Colors.role.surfaceHover}
              flexContainer
            />
          </View>
        ) : (
          <View style={styles.scrollViewContainer}>
            <ScrollView>
              <RenderAlerts />
              <Button
                variant="rectangular"
                size="large"
                state="default"
                onPress={handleShowNewAlertModal}
                leftIcon={
                  <Icon 
                    name="Add" 
                    size={20} 
                    color={Colors.role.textStrong} 
                    style={{marginRight: tokens.spacing.md}} 
                  />
                }
                style={styles.createAlertButton}
              >
                {t('alerts.setAlert')}
              </Button>
            </ScrollView>
          </View>
        )}
        <BottomComponentBar
          backButton={
            <Button
              variant="rounded"
              state="active"
              onPress={onClose}
              style={styles.backButton}
              leftIcon={<Icon name="Chevron Left" size={24} color={Colors.role.textOnInverse} />}
            />
          }
        />
      </View>
      {
        <NewAlertModal
          newAlertModalVisible={newAlertModalVisible}
          onClose={handleCloseNewAlertModal}
          workspaceAssetId={workspaceAssetId}
          routineId={routineId}
          productId={productId}
        />
      }

      {alertSummaryId && (
        <EditAlertModal
          editAlertModalVisible={editAlertModalVisible}
          onClose={handleCloseEditAlertModal}
          workspaceAssetId={workspaceAssetId}
          routineId={routineId}
          alertId={alertSummaryId}
          editAlertModalStyle={editAlertModalStyle}
          productId={productId}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.role.surfaceHover,
  },
  scrollViewContainer: {
    flex: 1,
    margin: tokens.spacing.xl,
    paddingBottom: 80, // Space for bottom bar
  },
  createAlertButton: {
    marginTop: tokens.spacing.md,
  },
  backButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: tokens.spacing.lg,
  },
});
